import { useEffect, useState } from 'react';
import type { Me } from '../lib/api';
import { API_BASE } from '../lib/api';
import TrackerSyncPanel from './TrackerSyncPanel';
import DashboardTab from './DashboardTab';
import TrackerHistoryPanel from './TrackerHistoryPanel';

/**
 * Migration Tracker Sheet — the EDIT surface. A full mirror of the Master
 * workbook: the Master tab is editable (gold admin columns up front so they're
 * reachable), and the workbook's other tabs are read-only tabs alongside it.
 * Access limited to the named allow-list (kept in sync with MigrationTracker).
 */
const ALLOW = ['ahmed.hamad@longbeach.gov'];
const STATUS = ['', 'Not started', 'In progress', 'Scheduled', 'Migrated', 'Verified', 'On hold', 'N/A'];
const ADMIN = [
  { f: 'status', l: 'Migration Status' }, { f: 'assigned_tech', l: 'Assigned Tech' },
  { f: 'cutover_date', l: 'Scheduled Cutover' }, { f: 'verified_by', l: 'Verified By' }, { f: 'notes', l: 'Admin Notes' },
];

function MasterEdit({ canEdit }: { canEdit: boolean }) {
  const [cols, setCols] = useState<string[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 50;
  const load = (off: number, q: string) => {
    setLoading(true);
    fetch(`${API_BASE}/api/migration/tracker?search=${encodeURIComponent(q)}&offset=${off}&limit=${LIMIT}`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => { if (j.cols) setCols(j.cols); setRows(j.rows || []); setTotal(j.total || 0); setOffset(off); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(0, ''); /* eslint-disable-next-line */ }, []);
  const save = (k: string, field: string, value: string, idx: number) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: value, _saved: false } : r)));
    fetch(`${API_BASE}/api/migration/tracker/update`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_key: k, field, value }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('save'))))
      .then(() => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, _saved: true } : r))))
      .catch(() => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, _err: true } : r))));
  };
  const gold = 'bg-yellow-50 dark:bg-yellow-900/40';
  const inp = 'bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400';
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-xs text-gray-500">{loading ? 'Loading…' : `${total.toLocaleString()} users · ${total ? offset + 1 : 0}–${Math.min(offset + LIMIT, total)} · gold columns are editable & save live`}</div>
        <form onSubmit={(e) => { e.preventDefault(); load(0, search); }} className="flex gap-2">
          <input className="input-sm" placeholder="Search any field…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="btn-secondary" type="submit">Search</button>
        </form>
      </div>
      <div className="overflow-auto border border-tnh-border dark:border-tnh-border-dark rounded" style={{ maxHeight: 600 }}>
        <table className="text-[11px] whitespace-nowrap">
          <thead className="sticky top-0 z-20 bg-tnh-card dark:bg-tnh-card-dark">
            <tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              <th className="px-2 py-1 text-left font-semibold sticky left-0 z-30 bg-tnh-card dark:bg-tnh-card-dark">Display Name</th>
              <th className="px-2 py-1 text-left font-semibold">Dept</th>
              <th className="px-2 py-1 text-left font-semibold">Ext</th>
              <th className="px-2 py-1 text-left font-semibold">Verdict</th>
              {ADMIN.map((a) => <th key={a.f} className={`px-2 py-1 text-left font-semibold text-yellow-900 dark:text-yellow-100 ${gold}`}>{a.l}</th>)}
              {cols.map((c, j) => <th key={j} className="px-2 py-1 text-left font-semibold text-gray-600 dark:text-gray-300">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const s = r.sys || [];
              return (
                <tr key={r.k} className={`border-b border-tnh-border/30 dark:border-tnh-border-dark/30 ${r._saved ? 'ring-1 ring-green-400' : ''}`}>
                  <td className="px-2 py-0.5 sticky left-0 bg-tnh-card dark:bg-tnh-card-dark font-medium">{s[3]}</td>
                  <td className="px-2 py-0.5">{s[0]}</td>
                  <td className="px-2 py-0.5">{s[6]}</td>
                  <td className="px-2 py-0.5">{String(s[5] || '').split(' ')[0]}</td>
                  <td className={`px-1 ${gold}`}>
                    <select disabled={!canEdit} value={r.status} onChange={(e) => save(r.k, 'status', e.target.value, i)} className={`${inp} [&>option]:text-gray-900`}>
                      {STATUS.map((x) => <option key={x} value={x}>{x || '—'}</option>)}
                    </select>
                  </td>
                  <td className={`px-1 ${gold}`}><input disabled={!canEdit} defaultValue={r.assigned_tech} onBlur={(e) => e.target.value !== r.assigned_tech && save(r.k, 'assigned_tech', e.target.value, i)} className={`${inp} w-24`} /></td>
                  <td className={`px-1 ${gold}`}><input disabled={!canEdit} type="date" defaultValue={r.cutover_date} onBlur={(e) => e.target.value !== r.cutover_date && save(r.k, 'cutover_date', e.target.value, i)} className={`${inp} w-28 dark:[color-scheme:dark]`} /></td>
                  <td className={`px-1 ${gold}`}><input disabled={!canEdit} defaultValue={r.verified_by} onBlur={(e) => e.target.value !== r.verified_by && save(r.k, 'verified_by', e.target.value, i)} className={`${inp} w-24`} /></td>
                  <td className={`px-1 ${gold}`}><input disabled={!canEdit} defaultValue={r.notes} onBlur={(e) => e.target.value !== r.notes && save(r.k, 'notes', e.target.value, i)} className={`${inp} w-40`} /></td>
                  {s.map((v: string, j: number) => <td key={j} className="px-2 py-0.5 max-w-[260px] truncate text-gray-600 dark:text-gray-400" title={v}>{v}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {total > LIMIT && (
        <div className="flex gap-2 justify-center mt-3">
          <button className="btn-secondary" disabled={offset === 0 || loading} onClick={() => load(Math.max(0, offset - LIMIT), search)}>Prev</button>
          <button className="btn-secondary" disabled={offset + LIMIT >= total || loading} onClick={() => load(offset + LIMIT, search)}>Next</button>
        </div>
      )}
    </div>
  );
}

function RefSheet({ tab }: { tab: string }) {
  const [header, setHeader] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const LIMIT = 200;
  const load = (off: number, q: string) => {
    setLoading(true);
    fetch(`${API_BASE}/api/migration/refsheet?tab=${encodeURIComponent(tab)}&offset=${off}&limit=${LIMIT}&search=${encodeURIComponent(q)}`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => { setHeader(j.header || []); setRows(j.rows || []); setTotal(j.total || 0); setOffset(off); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(0, ''); setSearch(''); /* eslint-disable-next-line */ }, [tab]);
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-xs text-gray-500">{loading ? 'Loading…' : `${total.toLocaleString()} rows`}</div>
        <form onSubmit={(e) => { e.preventDefault(); load(0, search); }} className="flex gap-2">
          <input className="input-sm" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="btn-secondary" type="submit">Search</button>
        </form>
      </div>
      <div className="overflow-auto border border-tnh-border dark:border-tnh-border-dark rounded" style={{ maxHeight: 600 }}>
        <table className="text-[11px] whitespace-nowrap">
          <thead className="sticky top-0 z-10 bg-tnh-card dark:bg-tnh-card-dark">
            <tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              {header.map((h, j) => <th key={j} className="px-2 py-1 text-left font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-tnh-border/30 dark:border-tnh-border-dark/30">
                {r.map((v, j) => <td key={j} className="px-2 py-0.5 max-w-[320px] truncate" title={v}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > LIMIT && (
        <div className="flex gap-2 justify-center mt-3">
          <button className="btn-secondary" disabled={offset === 0 || loading} onClick={() => load(Math.max(0, offset - LIMIT), search)}>Prev</button>
          <button className="btn-secondary" disabled={offset + LIMIT >= total || loading} onClick={() => load(offset + LIMIT, search)}>Next</button>
        </div>
      )}
    </div>
  );
}

export default function MigrationTrackerSheet({ me }: { me: Me }) {
  const allowed = me.role === 'super_admin' || ALLOW.includes((me.email || '').toLowerCase());
  const canEdit = me.role === 'super_admin' || me.role === 'tenant_admin' || me.role === 'tenant_user_rw';
  const [tabs, setTabs] = useState<string[]>([]);
  const [active, setActive] = useState('Dashboard');

  useEffect(() => {
    if (!allowed) return;
    fetch(`${API_BASE}/api/migration/refsheet`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => setTabs(j.tabs || [])).catch(() => {});
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto mt-16 card p-8 text-center">
        <div className="text-lg font-semibold">Migration Tracker Sheet — access restricted</div>
        <p className="text-sm text-gray-500 mt-2">Limited to named administrators. Ask Ahmed (Long Beach IT) to add <span className="font-mono">{me.email}</span>.</p>
      </div>
    );
  }
  const ALLTABS = ['Dashboard', 'Master Migration', ...tabs];
  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Collaboration Master Tracker</h1>
        <p className="text-sm text-gray-500">Full mirror of the Master workbook · Master tab is editable (gold columns), other tabs are read-only.</p>
      </div>
      <TrackerSyncPanel me={me} apiBase={API_BASE} />
      <TrackerHistoryPanel me={me} apiBase={API_BASE} />
      <div className="flex gap-1 flex-wrap border-b border-tnh-border dark:border-tnh-border-dark">
        {ALLTABS.map((t) => (
          <button key={t} onClick={() => setActive(t)}
            className={`px-3 py-1.5 text-sm rounded-t-md ${active === t ? 'bg-tnh-accent/10 text-tnh-accent font-medium border-b-2 border-tnh-accent' : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'}`}>
            {t === 'Master Migration' ? 'Master (edit)' : t}
          </button>
        ))}
      </div>
      <div className="card p-3">
        {active === 'Dashboard' ? <DashboardTab canEdit={canEdit} />
          : active === 'Master Migration' ? <MasterEdit canEdit={canEdit} />
          : <RefSheet tab={active} />}
      </div>
    </div>
  );
}
